import { useState } from "react"
import { BadgeCheck, BadgeX, Star, StarOff, Eye, X, MapPin, Phone, Mail, Globe, ExternalLink } from "lucide-react"
import { useGetBusinessesQuery, useGetBusinessByIdQuery, useVerifyBusinessMutation, useFeatureBusinessMutation } from "../features/admin/adminApi"
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'

// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
})

export default function BusinessesPage() {
  const [page, setPage] = useState(1)
  const [verified, setVerified] = useState<string>("")
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null)

  const { data, isLoading } = useGetBusinessesQuery({ page, verified: verified || undefined })
  const { data: businessDetails, isLoading: isLoadingDetails } = useGetBusinessByIdQuery(selectedBusinessId!, {
    skip: !selectedBusinessId,
  })
  const [verifyBusiness] = useVerifyBusinessMutation()
  const [featureBusiness] = useFeatureBusinessMutation()

  const businesses = data?.businesses ?? []
  const totalPages = data?.totalPages ?? 1

  const handleVerify = async (uid: string, currentlyVerified: boolean) => {
    const action = currentlyVerified ? "Unverify" : "Verify"
    if (!window.confirm(`${action} this business?`)) return
    await verifyBusiness({ uid, verify: !currentlyVerified })
  }

  const handleFeature = async (uid: string, currentlyFeatured: boolean) => {
    await featureBusiness({ uid, feature: !currentlyFeatured })
  }

  const hasValidLocation = businessDetails?.location.latitude && businessDetails?.location.longitude &&
    businessDetails.location.latitude !== 0 && businessDetails.location.longitude !== 0

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Businesses</h1>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex gap-2">
          <select
            value={verified}
            onChange={(e) => { setVerified(e.target.value); setPage(1) }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All</option>
            <option value="true">Verified</option>
            <option value="false">Unverified</option>
          </select>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading businesses...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Location</th>
                  <th className="px-4 py-3 text-left">Verified</th>
                  <th className="px-4 py-3 text-left">Featured</th>
                  <th className="px-4 py-3 text-left">Joined</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {businesses.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No businesses found.</td></tr>
                ) : (
                  businesses.map((b) => (
                    <tr key={b.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{b.name}</td>
                      <td className="px-4 py-3 text-gray-600">{[b.city, b.state].filter(Boolean).join(", ") || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${b.isVerified ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          {b.isVerified ? "Verified" : "Unverified"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${b.isFeatured ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500"}`}>
                          {b.isFeatured ? "Featured" : "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{new Date(b.createdAt).toLocaleDateString()}</td>
                      <td className="px-4 py-3 flex gap-1">
                        <button
                          onClick={() => setSelectedBusinessId(b.id)}
                          title="View Details"
                          className="p-1.5 rounded-lg transition-colors text-blue-600 hover:bg-blue-50"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleVerify(b.id, b.isVerified)}
                          title={b.isVerified ? "Unverify" : "Verify"}
                          className={`p-1.5 rounded-lg transition-colors ${b.isVerified ? "text-gray-500 hover:bg-gray-100" : "text-green-600 hover:bg-green-50"}`}
                        >
                          {b.isVerified ? <BadgeX size={16} /> : <BadgeCheck size={16} />}
                        </button>
                        <button
                          onClick={() => handleFeature(b.id, b.isFeatured)}
                          title={b.isFeatured ? "Unfeature" : "Feature"}
                          className={`p-1.5 rounded-lg transition-colors ${b.isFeatured ? "text-yellow-500 hover:bg-yellow-50" : "text-gray-400 hover:bg-gray-100"}`}
                        >
                          {b.isFeatured ? <Star size={16} /> : <StarOff size={16} />}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50">Previous</button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Business Details Modal */}
      {selectedBusinessId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Business Details</h2>
              <button
                onClick={() => setSelectedBusinessId(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {isLoadingDetails ? (
              <div className="p-8 text-center text-gray-500">Loading details...</div>
            ) : businessDetails ? (
              <div className="p-6 space-y-6">
                {/* Business Info */}
                <div className="flex items-start gap-4">
                  {businessDetails.business_logo && (
                    <img
                      src={businessDetails.business_logo}
                      alt={businessDetails.business_name}
                      className="w-20 h-20 rounded-lg object-cover border border-gray-200"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{businessDetails.business_name}</h3>
                    <div className="flex gap-2 mb-3">
                      {businessDetails.isVerified && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          ✓ Verified
                        </span>
                      )}
                      {businessDetails.isFeatured && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          ⭐ Featured
                        </span>
                      )}
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {businessDetails.business_category_name}
                      </span>
                    </div>
                    <p className="text-gray-600">{businessDetails.business_description}</p>
                  </div>
                </div>

                {/* Location */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin size={18} className="text-gray-600" />
                    Location
                  </h4>
                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <p>{businessDetails.location.address}</p>
                    <p>{[businessDetails.location.city, businessDetails.location.state, businessDetails.location.country].filter(Boolean).join(", ")}</p>
                    {hasValidLocation && (
                      <p className="text-xs text-gray-500">
                        Coordinates: {businessDetails.location.latitude}, {businessDetails.location.longitude}
                      </p>
                    )}
                  </div>

                  {/* Map */}
                  {hasValidLocation ? (
                    <div className="h-64 rounded-lg overflow-hidden border border-gray-200">
                      <MapContainer
                        center={[businessDetails.location.latitude!, businessDetails.location.longitude!]}
                        zoom={15}
                        style={{ height: '100%', width: '100%' }}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker position={[businessDetails.location.latitude!, businessDetails.location.longitude!]}>
                          <Popup>{businessDetails.business_name}</Popup>
                        </Marker>
                      </MapContainer>
                    </div>
                  ) : (
                    <div className="h-64 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-gray-500 text-sm">
                      <div className="text-center">
                        <MapPin size={32} className="mx-auto mb-2 text-gray-400" />
                        <p>Location coordinates not available</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Contact Information */}
                {businessDetails.contact && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Contact Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {businessDetails.contact.email && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail size={16} className="text-gray-400" />
                          <a href={`mailto:${businessDetails.contact.email}`} className="hover:text-blue-600">
                            {businessDetails.contact.email}
                          </a>
                        </div>
                      )}
                      {businessDetails.contact.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone size={16} className="text-gray-400" />
                          <a href={`tel:${businessDetails.contact.phone}`} className="hover:text-blue-600">
                            {businessDetails.contact.phone}
                          </a>
                        </div>
                      )}
                      {businessDetails.contact.secondPhone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone size={16} className="text-gray-400" />
                          <a href={`tel:${businessDetails.contact.secondPhone}`} className="hover:text-blue-600">
                            {businessDetails.contact.secondPhone} (Secondary)
                          </a>
                        </div>
                      )}
                      {businessDetails.contact.whatsapp && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone size={16} className="text-green-500" />
                          <a href={`https://wa.me/${businessDetails.contact.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 flex items-center gap-1">
                            {businessDetails.contact.whatsapp} (WhatsApp)
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      )}
                      {businessDetails.contact.website && (
                        <div className="flex items-center gap-2 text-gray-600 md:col-span-2">
                          <Globe size={16} className="text-gray-400" />
                          <a href={businessDetails.contact.website} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 flex items-center gap-1">
                            {businessDetails.contact.website}
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Owner Information */}
                {businessDetails.owner && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Business Owner</h4>
                    <div className="text-sm space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Name:</span>
                        <span className="text-gray-600">{businessDetails.owner.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Email:</span>
                        <a href={`mailto:${businessDetails.owner.email}`} className="text-blue-600 hover:underline">
                          {businessDetails.owner.email}
                        </a>
                      </div>
                      {businessDetails.owner.phone && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-700">Phone:</span>
                          <a href={`tel:${businessDetails.owner.phone}`} className="text-blue-600 hover:underline">
                            {businessDetails.owner.phone}
                          </a>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Owner ID:</span>
                        <span className="text-gray-500 text-xs font-mono">{businessDetails.owner.uid}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="border-t pt-4 text-xs text-gray-500 space-y-1">
                  <p>Created: {new Date(businessDetails.createdAt).toLocaleString()}</p>
                  <p>Last Updated: {new Date(businessDetails.updatedAt).toLocaleString()}</p>
                  <p>Business ID: <span className="font-mono">{businessDetails.id}</span></p>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-red-500">Failed to load business details</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
